import { useProductForm } from "../../contexts/ProductFormContext";
import { FaAngleRight, FaChevronDown } from "react-icons/fa";
import { IoAddOutline } from "react-icons/io5";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { getAllCategory } from "../../redux/slices/categorySlice";
import { useEffect, useRef, useState } from "react";
import Select from "react-select";
import { getAllBrands } from "../../redux/slices/brandSlice";
import loading from "../../animations/loading.json";
import Lottie from "lottie-react";
import { IoIosCloseCircle } from "react-icons/io";
import { useFileUpload } from "../../hooks/useFileUpload";
import { useVideoUpload } from "../../hooks/useVideoUpload";
import { API } from "../../configs/endpoints";
import type { Image } from "../../types/common/image";

export const BasicInfoTab = () => {
  const dispatch = useAppDispatch();
  const { product, setProduct, selectedCategory, setSelectedCategory } =
    useProductForm();
  const { allBrands } = useAppSelector((state) => state.brand);
  const { allCategory } = useAppSelector((state) => state.category);

  useEffect(() => {
    dispatch(getAllCategory({ isActive: true }));
    dispatch(getAllBrands({ isActive: true }));
  }, [dispatch]);

  const [pathIds, setPathIds] = useState<string[]>([]);
  const [chooseCate, setChooseCate] = useState<string | null>(null);
  const [final, setFinal] = useState<boolean>(false);
  const [isChooseCate, setIsChooseCate] = useState<boolean>(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const { upload: uploadImages, isUploading: isUploadImage } = useFileUpload<{
    images: Image[];
  }>({
    endpoint: API.product.upload,
    kind: "image",
    maxFiles: 10,
  });

  const handleChooseImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    const result = await uploadImages(files);
    e.target.value = "";
    if (result.ok && result.data?.images) {
      setProduct((prev) => ({
        ...prev,
        product: {
          ...prev.product,
          images: [...prev.product.images, ...result.data!.images],
        },
      }));
    }
  };

  const inputVideo = useRef<HTMLInputElement>(null);
  const {
    upload: uploadVideo,
    isUploading: isUploadVideo,
    progress: videoProgress,
  } = useVideoUpload();

  const handleChooseVideo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    const result = await uploadVideo(file);
    e.target.value = "";
    if (result) {
      setProduct((prev) => ({
        ...prev,
        product: {
          ...prev.product,
          video: { url: result.url, public_id: result.public_id },
        },
      }));
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-lg">
      <h2 className="text-lg text-black font-semibold">Thông tin cơ bản</h2>
      <div className="space-y-2">
        <label className="block text-sm text-gray-700 font-semibold">
          Tên sản phẩm
        </label>
        <input
          type="text"
          name="name"
          value={product.product.name}
          onChange={(e) =>
            setProduct((prev) => ({
              ...prev,
              product: {
                ...prev.product,
                name: e.target.value,
              },
            }))
          }
          className="p-2 border border-gray-200 rounded-xl w-full focus:outline-none focus:border-blue-500 transition-colors duration-300 placeholder:text-sm placeholder:tracking-tighter placeholder:text-gray-400"
          placeholder="Nhập tên sản phẩm"
        />
      </div>
      <div className="space-y-2 relative">
        <label className="block text-sm text-gray-700 font-semibold">
          Danh mục sản phẩm
        </label>
        <button
          type="button"
          onClick={() => {
            setIsChooseCate(!isChooseCate);
          }}
          className="w-full flex items-center justify-between p-2 border border-gray-200 rounded-xl hover:border-blue-500 transition-colors duration-300 cursor-pointer text-sm text-gray-700"
        >
          {selectedCategory ? (
            <p className="text-sm text-gray-700">
              {allCategory
                .find((cate) => cate._id === selectedCategory)
                ?.fullPath.join(" > ")}
            </p>
          ) : (
            <p className="text-sm text-gray-700">Chọn danh mục</p>
          )}
          <FaChevronDown
            className={`text-sm text-gray-700 transition-all duration-300 `}
          />
        </button>

        <div
          className={`absolute z-50 top-full mt-0.5 rounded-xl border border-gray-100 p-5 bg-white w-full transition-transform duration-500 ease-in-out ${isChooseCate ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-2 pointer-events-none"}`}
        >
          <div className="w-full grid grid-cols-1 md:grid-cols-4 gap-5 md:divide-x divide-gray-200">
            <div className="space-y-2 p-2 max-h-60 overflow-y-auto scrollbar-custom">
              {allCategory.length > 0 &&
                allCategory
                  .filter((cate) => !cate.parentId)
                  .map((cate) => (
                    <button
                      onClick={() => {
                        setPathIds([cate._id]);
                        setChooseCate(cate._id);
                        if (cate.hasChildren) {
                          // Có con → hiện cột 2 để chọn tiếp
                          setFinal(false);
                          setSelectedCategory("");
                        } else {
                          // Danh mục lá → chọn luôn
                          setFinal(true);
                          setSelectedCategory(cate._id);
                        }
                      }}
                      key={cate._id}
                      type="button"
                      className={`w-full flex items-center justify-between gap-2 p-2 border rounded-lg cursor-pointer text-sm transition-colors duration-300 ${pathIds.includes(cate._id) ? "border-blue-500 text-blue-500" : "border-gray-200 text-gray-700"}`}
                    >
                      <div className="flex items-center gap-2">
                        <img
                          src={cate.image?.url}
                          alt="Ảnh danh mục"
                          className="h-10 w-10 border border-gray-200 rounded-xl object-cover"
                        />
                        <p>{cate.name}</p>
                      </div>
                      {cate.hasChildren && <FaAngleRight />}
                    </button>
                  ))}
            </div>
            <div className="space-y-2 p-2 max-h-60 overflow-y-auto scrollbar-custom">
              {chooseCate &&
                allCategory
                  .filter((cate) => cate.parentId === chooseCate)
                  .map((cate) => (
                    <button
                      type="button"
                      key={cate._id}
                      onClick={() => {
                        setPathIds((prev) => [...prev, cate._id]);
                        setFinal(true);
                        setSelectedCategory(cate._id);
                      }}
                      className={`w-full flex items-center gap-2 p-2 border rounded-lg cursor-pointer text-sm transition-colors duration-300 ${pathIds.includes(cate._id) ? "border-blue-500 text-blue-500" : "border-gray-200 text-gray-700"}`}
                    >
                      <img
                        src={cate.image?.url}
                        alt="Ảnh danh mục"
                        className="border border-gray-200 rounded-xl h-10 w-10 object-cover"
                      />
                      <p>{cate.name}</p>
                    </button>
                  ))}
            </div>
          </div>
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setIsChooseCate(false);
              }}
              className="p-2 border border-gray-200 rounded-xl text-sm text-gray-700 hover:text-red-500 hover:border-red-500 transition-colors duration-300 cursor-pointer"
            >
              Hủy bỏ
            </button>
            <button
              type="button"
              onClick={() => {
                setProduct((prev) => ({
                  ...prev,
                  product: {
                    ...prev.product,
                    categoryId: selectedCategory,
                  },
                }));
                setIsChooseCate(false);
              }}
              className={`p-2 transition-colors duration-300 cursor-pointer text-sm rounded-lg ${final ? "bg-blue-500 text-white hover:bg-blue-600" : "text-gray-400 bg-gray-100"}`}
            >
              Xác nhận
            </button>
          </div>
        </div>
      </div>
      {/* image, video */}

      <div className="space-y-2">
        <div className="space-y-2">
          <label className="block text-sm text-gray-700 font-semibold">
            Chọn thương hiệu
          </label>
          <Select
            required
            className="p-2 text-sm text-gray-700 rounded-lg cursor-pointer"
            options={allBrands.map((brand) => ({
              value: brand._id,
              label: brand.name,
            }))}
            value={
              allBrands
                .map((brand) => ({
                  value: brand._id,
                  label: brand.name,
                }))
                .find((opt) => opt.value === product.product.brandId) || null
            }
            onChange={(selected) =>
              setProduct((prev) => ({
                ...prev,
                product: {
                  ...prev.product,
                  brandId: selected?.value || "",
                },
              }))
            }
            placeholder="Tìm và chọn thương hiệu..."
            isSearchable
          />
        </div>
        <div className="space-y-2">
          <label className="block text-sm text-gray-700 font-semibold">
            Ảnh sản phẩm
          </label>
          <div className="p-2 bg-blue-50 rounded-lg w-full flex items-center gap-2">
            <div className="h-20 w-20">
              <input
                onChange={handleChooseImage}
                ref={inputRef}
                multiple
                accept="image/*"
                type="file"
                className="hidden"
              />

              {isUploadImage ? (
                <Lottie
                  animationData={loading}
                  loop={true}
                  className="w-full h-full"
                />
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    inputRef.current?.click();
                  }}
                  className="border h-20 w-20 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 transition-colors duration-300 p-5 text-xl text-gray-700 flex items-center justify-center hover:text-blue-500"
                >
                  <IoAddOutline />
                </button>
              )}
            </div>
            <div className="w-full flex items-center gap-2 overflow-x-auto scrollbar-custom p-2">
              {product.product.images.length > 0 &&
                product.product.images.map((image) => (
                  <div
                    key={image.public_id}
                    className="w-20 h-20 relative shrink-0"
                  >
                    <img
                      src={image.url}
                      alt="Ảnh sản phẩm"
                      className="w-full h-full object-cover rounded-lg border border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setProduct((prev) => ({
                          ...prev,
                          product: {
                            ...prev.product,
                            images: prev.product.images.filter(
                              (item) => item.public_id !== image.public_id,
                            ),
                          },
                        }));
                      }}
                      className="absolute text-xl -top-2 -right-2 text-red-500 cursor-pointer hover:text-red-600"
                    >
                      <IoIosCloseCircle />
                    </button>
                  </div>
                ))}
            </div>
          </div>
        </div>
        <div className="space-y-2">
          <label className="block text-sm text-gray-700 font-semibold">
            Video
          </label>
          <div className="p-2 bg-blue-50 rounded-lg w-full">
            <input
              ref={inputVideo}
              accept="video/*"
              type="file"
              className="hidden"
              onChange={handleChooseVideo}
            />
            {isUploadVideo ? (
              <div className="flex flex-col items-center gap-2 w-40">
                <Lottie
                  animationData={loading}
                  loop={true}
                  className="h-16 w-16"
                />
                <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-500 transition-all duration-200"
                    style={{ width: `${videoProgress}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500">
                  Đang tải lên... {videoProgress}%
                </p>
              </div>
            ) : (
              <div className="">
                {product.product.video?.url ? (
                  <div className="relative w-40 h-40">
                    <video
                      src={product.product.video?.url}
                      className="w-40 h-40 rounded-lg object-cover"
                      controls
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setProduct((prev) => ({
                          ...prev,
                          product: {
                            ...prev.product,
                            video: null,
                          },
                        }));
                      }}
                      className="absolute -top-2 -right-2 text-xl text-red-500 hover:text-red-600 cursor-pointer transition-colors duration-300"
                    >
                      <IoIosCloseCircle />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      inputVideo.current?.click();
                    }}
                    className="border h-20 w-20 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 transition-colors duration-300 p-5 text-xl text-gray-700 flex items-center justify-center hover:text-blue-500"
                  >
                    <IoAddOutline />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
